<template>
  <page-loader>
    <filter-sidebar
      v-model="filterDrawerOpen"
      :loading="isLoading"
      :show-clear="showClearFilters"
      @clear="onClearFilters"
    >
      <v-container>
        <v-row>
          <v-col cols="12">
            <search-input v-model="query.searchTerm" label="Search" class="mt-2" />
          </v-col>
          <v-col cols="12">
            <v-subheader>Details</v-subheader>
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.target_site_id"
              label="Target Domain"
              clearable
              :items="targetDomainOptions"
              hide-details
            />
          </v-col>
          <v-col cols="12">
            <select-list
              v-model="query.params.campaign_type"
              label="Campaign Type"
              clearable
              :items="campaignTypeOptions"
              hide-details
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <v-divider />
            <v-subheader>Status</v-subheader>
          </v-col>
          <v-col cols="6">
            <select-list
              v-model="query.params.live_status"
              label="Live Status"
              clearable
              :items="liveStatusOptions"
              hide-details
            />
          </v-col>
          <v-col cols="6">
            <select-list
              v-model="query.params.payment_status"
              label="Payment Status"
              clearable
              :items="paymentStatusOptions"
              hide-details
            />
          </v-col>
          <v-col cols="12" class="pb-0">
            <v-divider />
            <v-subheader>Live Date</v-subheader>
          </v-col>
          <v-col cols="6">
            <date-picker v-model="query.params.live_dateGreaterThan" format="DDMMYYYY" label="From" clearable />
          </v-col>
          <v-col cols="6">
            <date-picker v-model="query.params.live_dateLessThan" format="DDMMYYYY" label="To" clearable />
          </v-col>
        </v-row>
      </v-container>
    </filter-sidebar>

    <v-container>
      <v-row>
        <v-col>
          <filter-chips :data="filterChipsData" @clear-chip="$_filterChips_clearChip">
            <v-badge color="accent" bordered overlap class="mr-4" :content="$_queryHandler_numberOfQueries">
              <v-btn color="accent" depressed small title="Filters" @click="filterDrawerOpen = true">
                <v-icon left small>filter_alt</v-icon>
                <span>Filters</span>
              </v-btn>
            </v-badge>

            <v-btn color="primary" title="Export" small depressed @click="exportBacklinks">
              <v-icon small left>file_upload</v-icon>
              <span>Export</span>
            </v-btn>
          </filter-chips>
        </v-col>
      </v-row>
    </v-container>

    <v-container>
      <v-row>
        <v-col>
          <v-card outlined>
            <v-card-title>Backlinks</v-card-title>
            <v-data-table
              :headers="tableHeaders"
              :item-class="getTableRowClass"
              :items="links"
              :items-per-page="pagination.perPage"
              :page="pagination.currentPage"
              :server-items-length="pagination.total"
              :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }"
              disable-sort
              @click:row="onTableRowClick"
              @update:items-per-page="onUpdateItemsPerPage"
              @update:page="onUpdatePage"
            >
              <template v-slot:item.live_date="{ value }">
                <span v-if="value">{{ formatDate(value) }}</span>
                <span v-else>-</span>
              </template>

              <template v-slot:item.target_site_id="{ value, item }">
                <v-tooltip v-if="value" bottom>
                  <template #activator="{ on }">
                    <span v-on="on">{{ getTargetDomainFromId(value) }}</span>
                  </template>
                  <span>{{ [getTargetDomainFromId(value), item.target_url].join('/') }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>

              <template v-slot:item.referring_domain="{ value, item }">
                <v-tooltip v-if="value" bottom>
                  <template #activator="{ on }">
                    <span v-on="on">{{ value }}</span>
                  </template>
                  <span>{{ item.referring_url }}</span>
                </v-tooltip>
                <span v-else>-</span>
              </template>

              <template v-slot:item.campaign_type="{ value }">
                {{ getCampaignType(value) || '-' }}
              </template>

              <template v-slot:item.live_status="{ value, item }">
                <v-tooltip v-if="value" bottom>
                  <template #activator="{ on }">
                    <v-icon color="success" v-on="on">
                      check_circle_outline
                    </v-icon>
                  </template>
                  <span v-if="item.live_date">Live from {{ formatDate(item.live_date) }}</span>
                  <span v-else>No date specified</span>
                </v-tooltip>
                <v-icon v-else color="error">highlight_off</v-icon>
              </template>

              <template v-slot:item.domain_rating="{ value }">
                {{ value || '-' }}
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <fab-create v-if="isPermittedTo('create')" type="Backlink" @click="showCreateModal = true" />

    <generic-modal
      v-model="showCreateModal"
      title="Create New Backlink Entry"
      persistent
      confirm-text="Create Entry"
      @modal-confirm="onModalCreateConfirm"
      @modal-cancel="onModalCreateCancel"
    >
      <autocomplete-input :value="user.id" :items="userOptions" label="Owner" disabled />
      <span class="text-caption">Clicking "Create Entry" will redirect to the EDIT page for the new entry.</span>
    </generic-modal>
  </page-loader>
</template>

<script>
// Services
import { backlinks, sites } from '@/services/offers'
// Mixins
import FilterChipsHandler from '@/mixins/filter-chips'
import PermissionsHandler from '@/mixins/permissions-handler'
import QueryParamsHandler from '@/mixins/query-params-handler'
// Helpers
import { mapGetters, mapMutations, mapState } from 'vuex'
import { formatDate } from '@/utils/date'
import isSet from '@/utils/is-set'
// Components
import AutocompleteInput from '@/components/forms/autocomplete-input'
import DatePicker from '@/components/forms/date-picker'
import FabCreate from '@/components/fab-create'
import FilterChips from '@/components/filter-chips'
import FilterSidebar from '@/components/filter-sidebar'
import GenericModal from '@/components/modals/generic-modal'
import PageLoader from '@/components/page-loader'
import SearchInput from '@/components/forms/search-input'
import SelectList from '@/components/forms/select-list'

export default {
  name: 'LinkTracking',
  components: {
    AutocompleteInput,
    DatePicker,
    FabCreate,
    FilterChips,
    FilterSidebar,
    GenericModal,
    PageLoader,
    SelectList,
    SearchInput,
  },
  mixins: [FilterChipsHandler, PermissionsHandler, QueryParamsHandler],
  data: () => ({
    filterDrawerOpen: false,
    isLoading: false,
    links: [],
    liveStatusOptions: [
      { key: 'Live', value: 1 },
      { key: 'Not Live', value: 0 },
    ],
    paymentStatusOptions: [
      { key: 'Paid', value: 1 },
      { key: 'Unpaid', value: 0 },
    ],
    query: {
      params: {
        campaign_type: null,
        live_dateGreaterThan: null,
        live_dateLessThan: null,
        live_status: null,
        payment_status: null,
        target_site_id: null,
      },
    },
    queriesDefined: [
      'campaign_type',
      'live_dateGreaterThan',
      'live_dateLessThan',
      'live_status',
      'payment_status',
      'target_site_id',
    ],
    queryTypes: { live_status: 'number', payment_status: 'number' },
    showCreateModal: false,
    tableHeaders: [
      { text: 'Live Status', value: 'live_status' },
      { text: 'Live Date', value: 'live_date' },
      { text: 'Campaign Type', value: 'campaign_type' },
      { text: 'Target Domain', value: 'target_site_id' },
      { text: 'Referring Domain', value: 'referring_domain' },
      { text: 'Domain Rating', value: 'domain_rating' },
    ],
    targetDomainOptions: [],
    users: [],
  }),
  computed: {
    ...mapGetters('account', ['user']),
    ...mapGetters('options', ['getItemsForOption']),
    ...mapState('backlinks', ['schema']),
    campaignTypeOptions() {
      return this.getItemsForOption('campaignTypes')
    },
    filterChipsData() {
      return [
        {
          label: 'Search',
          model: 'query.searchTerm',
          value: this.query.searchTerm,
        },
        {
          label: 'Target Domain',
          model: 'query.params.target_site_id',
          options: this.targetDomainOptions,
          value: this.query.params.target_site_id,
        },
        {
          label: 'Campaign Type',
          model: 'query.params.campaign_type',
          options: this.campaignTypeOptions,
          value: this.query.params.campaign_type,
        },
        {
          label: 'Live Status',
          model: 'query.params.live_status',
          options: this.liveStatusOptions,
          value: this.query.params.live_status,
        },
        {
          label: 'Payment Status',
          model: 'query.params.payment_status',
          options: this.paymentStatusOptions,
          value: this.query.params.payment_status,
        },
        {
          label: 'Live Date from',
          model: 'query.params.live_dateGreaterThan',
          format: 'date',
          value: this.query.params.live_dateGreaterThan,
        },
        {
          label: 'Live Date to',
          model: 'query.params.live_dateLessThan',
          format: 'date',
          value: this.query.params.live_dateLessThan,
        },
      ]
    },
    newBacklinkProperties() {
      if (this.schema && this.schema.properties) {
        return Object.keys(this.schema.properties).reduce((acc, key) => {
          acc[key] = this.schema.properties[key].default ?? null
          return acc
        }, {})
      }
      return {}
    },
    showClearFilters() {
      return this.queriesDefined.some(filter => isSet(this.query.params[filter]))
    },
    userOptions() {
      /* Get only current user as option (Privacy) */
      return [{ key: this.user.name, value: this.user.id }]
    },
  },
  async created() {
    await this.fetchLinks()
    await this.getTargetDomainOptions()
    this.setPageLoading(false)
  },
  methods: {
    ...mapMutations('general', ['setPageLoading']),
    async createLink() {
      this.isLoading = true
      try {
        const { id } = await backlinks.createBacklink({
          ...this.newBacklinkProperties,
          user_id: this.user.id,
        })
        this.$notify({
          type: 'success',
          text: 'Successfully created new Backlink entry',
        })
        return id
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to create new Backlink entry',
        })
      }
      this.isLoading = false
      return undefined
    },
    getTargetDomainFromId(id) {
      const domain = this.targetDomainOptions.find(({ value }) => value === id)
      return domain ? domain.key : `ID #${id}`
    },
    async getTargetDomainOptions() {
      this.isLoading = true
      try {
        const { data } = await sites.getAllSites({ filters: { type: 'organic' } })
        this.targetDomainOptions = data.map(({ name, id }) => ({ key: name, value: id }))
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch Target Domain options',
        })
      }
      this.isLoading = false
    },
    async fetchLinks() {
      this.isLoading = true
      try {
        const { perPage, currentPage: page } = this.pagination
        const {
          data,
          pagination: { total },
        } = await backlinks.getBacklinks({
          page: perPage !== -1 ? page : undefined,
          perPage: perPage !== -1 ? perPage : undefined,
          filters: { ...this.query.params },
          search: this.query.searchTerm,
        })
        this.links = data
        this.pagination.total = total
      } catch (e) {
        this.$notify({
          type: 'error',
          text: 'Unable to fetch link tracking data',
        })
      }
      this.isLoading = false
    },
    exportBacklinks() {
      backlinks.exportBacklinks(this.query.params)
    },
    formatDate(date) {
      return formatDate(date, { format: 'DDMMYYYY' })
    },
    getCampaignType(slug) {
      const campaign = this.campaignTypeOptions.find(({ value }) => value === slug)
      return campaign ? campaign.key : slug
    },
    getTableRowClass(row) {
      return ['table-row', { inactive: row.live_status === 0 }]
    },
    getUser(id) {
      const user = this.users.find(user => user.id === id)
      return user ? user.name : `User ID: ${id}`
    },
    async handleQueryUpdate() {
      await this.fetchLinks()
    },
    onClearFilters() {
      this.queriesDefined.forEach(filter => {
        if (isSet(this.query.params[filter])) {
          this.query.params[filter] = undefined
        }
      })
    },
    onModalCreateCancel() {
      this.showCreateModal = false
    },
    async onModalCreateConfirm() {
      const id = await this.createLink()
      if (id) {
        this.$router.push({ name: 'backlink-tracking.manage', params: { id } })
      }
    },
    onTableRowClick({ id }) {
      this.$router.push({ name: 'backlink-tracking.manage', params: { id } })
    },
    async onUpdateItemsPerPage(perPage) {
      this.pagination.perPage = perPage
      await this.onUpdatePage(1)
    },
    async onUpdatePage(page) {
      this.pagination.currentPage = page
      await this.handleQueryUpdate()
    },
  },
}
</script>

<style lang="scss">
@import '~vuetify/src/styles/settings/_variables';

.table-row {
  cursor: pointer;
}
.inactive {
  background-color: rgba(map-get($red, 'lighten-5'), 0.5);
}
</style>
